import { create } from 'zustand';

/**
 * Asking before an action that cannot be taken back.
 *
 * `confirmAction` is awaited where the action is, so a handler reads top to bottom — ask, then
 * act — instead of splitting into "open a dialog" and "on confirm" halves. One host renders the
 * question: `ConfirmHost` on the public surface, `AdminConfirmHost` inside the console scope.
 *
 * The body names the consequence, not the action: "Their advertisement leaves your page" rather
 * than "Are you sure?". A reader who is asked whether they are sure learns nothing they did not
 * know when they pressed the button.
 */
export interface ConfirmRequest {
  title: string;
  body: string;
  /** The confirming button's words: the action itself, "Delete photo", never "OK". */
  confirm: string;
  cancel?: string;
  /** `danger` marks a destructive action; the public surface still never fills it with a status colour. */
  tone?: 'default' | 'danger';
  /** Prefix for the two buttons' `data-ee-feature`: `${feature}.confirm` and `${feature}.cancel`. */
  feature: string;
}

interface ConfirmState {
  request: ConfirmRequest | null;
  resolve: ((ok: boolean) => void) | null;
  answer: (ok: boolean) => void;
}

export const useConfirm = create<ConfirmState>((set, get) => ({
  request: null,
  resolve: null,
  answer: (ok) => {
    const { resolve } = get();
    set({ request: null, resolve: null });
    resolve?.(ok);
  },
}));

/** Resolves true when the reader confirms, false when they cancel or dismiss. */
export function confirmAction(request: ConfirmRequest): Promise<boolean> {
  // A second question while one is open answers the first "no": two stacked dialogs would leave
  // the reader confirming something they cannot see.
  useConfirm.getState().answer(false);
  return new Promise((resolve) => useConfirm.setState({ request, resolve }));
}
